import { useState, useEffect } from 'react';
import { B, radius } from '../../constants/brand';
import { api } from '../../api/client';
import { useApi } from '../../hooks/useApi';
import { Btn, Card, Input, Badge, Modal, ModalFooter, Spinner, ErrorMsg, Icon, ProgressBar, useToast } from '../ui';
import { formatInstant } from '../../utils/format';

// =============================================================
// GovWinIqPanel — the GovWin IQ Web Service API connection.
//
// The direct-pull intake beside the forwarded IntelliSearch email and the
// .xlsx upload. It holds the org's client id + secret and the web-service
// user's username + password (the password OAuth grant — the only grant the
// WSAPI offers), plus the saved-search ids to pull. Secrets are write-only:
// the API reports only whether one is stored, and leaving a secret field
// blank on save keeps what is there.
//
// A sync is a long request (paging saved searches against GovWin), so the
// button sends a long timeout and the result is also persisted server-side
// — the panel re-reads status afterwards rather than trusting the response.
// =============================================================

const SYNC_TIMEOUT_MS = 6 * 60 * 1000;
// An enrichment lap runs for minutes server-side and the request routinely
// outlives the edge; the panel fires it on a long timeout and polls status.
const LAP_TIMEOUT_MS = 25 * 60 * 1000;
const LAP_POLL_MS    = 15 * 1000;

const dt = (v) => {
  if (!v) return '—';
  return formatInstant(v) || String(v);
};

const note = (tone) => {
  const t = {
    info:    { bg: '#f8fafc', border: '#e2e8f0', color: B.textMd },
    success: { bg: '#f0fdf4', border: '#bbf7d0', color: B.green },
    warn:    { bg: '#fffbeb', border: '#fde68a', color: B.amber },
    error:   { bg: '#fef2f2', border: '#fecaca', color: B.red },
  }[tone] || {};
  return { marginTop: 10, padding: '8px 12px', borderRadius: radius.md, fontSize: 12,
           background: t.bg, color: t.color, border: `1px solid ${t.border}` };
};

const row = { display: 'grid', gridTemplateColumns: '170px 1fr', gap: '4px 12px', fontSize: 13, color: B.text };
const lbl = { color: B.textMd, fontWeight: 600 };

// A search cut off by a budget KEEPS ITS PLACE — its own read-from mark does
// not move and the next run re-reads the same window — so the wording says
// so rather than the old "continues" wording, which read as though the next run
// picked up where this one left off when it used to start from a later point.
const stopLabel = (s) => ({
  'done': 'complete', 'partial': 'partial — the searches that were cut off keep their place and are re-read next run', 'page-cap': 'page cap — the newest rows were read; the next run re-reads from the oldest row this one reached, so nothing is dropped',
  'call-cap': 'call budget — re-read next run', 'time-budget': 'time budget — re-read next run', 'rate-limited': 'GovWin rate limit — re-read next run',
  'auth-failed': 'authentication failed', 'not-configured': 'not configured', 'disabled': 'paused',
  'nothing-to-pull': 'nothing to pull', 'in-progress': 'already running',
}[s] || s || '—');

const num = (v) => (v === null || v === undefined) ? '—' : Number(v).toLocaleString();
// The enrichment job's document outcomes, in words (IQE.DOC_OUTCOMES on the API).
const docOutcomeLabel = (r) => ({
  'landed': 'landed', 'no-href': 'no download address', 'not-file': 'a web page / JSON, not a file',
  'too-big': 'over the size cap', 'error': 'download failed',
}[r] || r || '—');
// The API's wall-clock string (yyyy-MM-ddTHH:mm:ss, no zone) shown as a date;
// it is GovWin's clock, not the browser's, so it is not converted.
const wall = (s) => (typeof s === 'string' && s.length >= 10) ? `${s.slice(0, 10)} ${s.slice(11, 16)}`.trim() : '—';

// =============================================================
// EnrichmentSection — everything GovWin holds on each opportunity.
//
// The sync reads SUMMARIES (one call per 100 rows). GovWin
// prices one opportunity WITH its related content at 6-12 calls against the
// org's 4,000-call hour, so "pull everything" is a standing job: one bounded
// lap an hour over every GovWin IQ row we hold, newest first — details,
// contacts, related documents (downloaded), notices, contracts, milestones,
// places of performance — until it has reached them all, then it keeps the
// active bids fresh. Everything here is the server's own state; the switch
// is the `enrich` flag on the connection config.
// =============================================================
const EnrichmentSection = ({ s, reload }) => {
  const { toast } = useToast();
  const e = s.enrichment || {};
  const p = e.progress || {};
  const lim = e.limits || {};
  const [busy, setBusy] = useState(null);   // 'lap' | 'toggle' | null
  const on = s.enrich !== false && s.enabled !== false;
  const active = on && e.available && !e.progress?.done;

  // Poll while a lap is in flight or work remains — the hourly tick moves it.
  useEffect(() => {
    if (!active) return undefined;
    const t = setInterval(() => reload(), LAP_POLL_MS);
    return () => clearInterval(t);
  }, [active, reload]);

  const runLap = async () => {
    setBusy('lap');
    try {
      const r = await api.post('govwins/iq/enrich/lap', {}, { timeout: LAP_TIMEOUT_MS });
      if (r?.ran === false) toast.info(`Nothing to run right now (${r.reason}).`);
      else if (r?.lap) toast.success(`Lap done: ${num(r.lap.enriched)} enriched, ${num(r.lap.docsPulled)} documents landed · ${stopLabel(r.lap.stop)}`);
    } catch (err) {
      toast.info(`${err.message} — the lap continues server-side; progress below updates as it goes.`);
    } finally { setBusy(null); reload(); }
  };
  const toggle = async (next) => {
    setBusy('toggle');
    try {
      await api.put('govwins/iq/config', { enrich: next });
      toast.success(next ? 'Enrichment on — the next lap runs at :10 past the hour.' : 'Enrichment paused — rows already enriched are kept.');
    } catch (err) { toast.error(err.message); }
    finally { setBusy(null); reload(); }
  };

  const badge = !e.available ? <Badge color={B.amber}>needs migration 201</Badge>
    : !on ? <Badge color={B.amber}>paused</Badge>
    : e.leaseHeld ? <Badge color={B.navy}>lap in progress</Badge>
    : p.done ? <Badge color={B.green}>up to date</Badge>
    : <Badge color={B.green}>running — next lap on the hour</Badge>;
  const total = (e.enriched || 0) + (p.remaining ?? 0);

  return (
    <div style={{ marginTop: 16, paddingTop: 12, borderTop: `1px solid ${B.border}` }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
        <div>
          <div style={{ fontSize: 14, fontWeight: 700, color: B.text, display: 'flex', alignItems: 'center', gap: 8 }}>
            Everything GovWin holds {badge}
          </div>
          <div style={{ fontSize: 12, color: B.textMd, marginTop: 2 }}>
            The sync reads summaries. This reads <b>all</b> of it — details, contacts, related documents (downloaded into Documents), notices, contracts, milestones, places of performance — newest first, one bounded lap an hour, then keeps active bids fresh every {lim.reEnrichDays || 30} days.
            {e.matchedGate
              ? <> It reads only bids that <b>matched one of your searches</b> (keywords, NAICS, saved searches or the download marks) and are still open; a row that arrived without matching one (an import, the shelved historical walk's) gets a summary and a link, and “Pull from GovWin IQ” on any bid's Documents still reads that one by hand.</>
              : <> Until migration 202 applies it reads every GovWin IQ bid we hold.</>}
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          {on && e.available && (
            <Btn variant="secondary" size="sm" onClick={runLap} loading={busy === 'lap'} disabled={!!busy || e.leaseHeld} title={e.leaseHeld ? 'A lap is running now' : 'Run the next lap now instead of waiting for the hour'}>Run next lap now</Btn>
          )}
          {on
            ? <Btn variant="ghost" size="sm" onClick={() => toggle(false)} loading={busy === 'toggle'} disabled={!!busy}>Pause</Btn>
            : <Btn size="sm" onClick={() => toggle(true)} loading={busy === 'toggle'} disabled={!!busy || !s.configured || s.enabled === false}>Resume</Btn>}
        </div>
      </div>

      {e.available && (
        <div style={{ marginTop: 10 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <ProgressBar value={total > 0 ? ((e.enriched || 0) / total) * 100 : 0} max={100} color={p.done ? B.green : B.tealDk} label="Enrichment progress" />
            <span style={{ fontSize: 12, color: B.textMd, whiteSpace: 'nowrap' }}>
              {p.remaining === null || p.remaining === undefined ? 'measuring…' : p.done ? 'nothing outstanding' : `${num(p.remaining)} to go`}
            </span>
          </div>
          <div style={{ ...row, marginTop: 8 }}>
            <span style={lbl}>Outstanding</span>
            <span>
              {num(p.remaining)} opportunit{p.remaining === 1 ? 'y' : 'ies'} still to read
              {p.remainingLaps !== null && p.remainingLaps !== undefined && !p.done
                ? ` (about ${num(p.remainingLaps)} more lap${p.remainingLaps === 1 ? '' : 's'} ${p.estimateBasis === 'measured' ? `at the last lap's pace of ${num(p.perLapOpps)} per lap` : `at the ${num(lim.callsPerLap)}-call budget, ${num(p.perLapOpps)} per lap — measured after the first full lap`})`
                : ''}
            </span>
            <span style={lbl}>Read so far</span>
            <span><b>{num(e.enriched)} enriched</b> · {num(e.docsPulled)} documents landed{e.docsRecorded ? ` · ${num(e.docsRecorded)} recorded without a file` : ''}{e.failed ? ` · ${num(e.failed)} failed (retried after 24 h)` : ''}{e.noApiId ? ` · ${num(e.noApiId)} carry no API id yet` : ''} · {num(e.calls)} API calls over {num(e.laps)} lap{e.laps === 1 ? '' : 's'}</span>
            {e.docsNote && <><span style={lbl}>Documents</span><span style={{ color: e.docsPulled ? B.text : B.amber }}>{e.docsNote}{!e.docsPulled && e.docsRecorded ? ' Nothing has landed yet — the examples below say what the links answered.' : ''}</span></>}
            <span style={lbl}>Last lap</span>
            <span>{e.lastLapUTC ? `${dt(e.lastLapUTC)} · ${num(e.lastLapEnriched)} read · stopped on ${stopLabel(e.lastLapStop)}` : '—'}{on && !e.leaseHeld && !p.done ? ' · the next one runs at :10 past the hour' : ''}</span>
            <span style={lbl}>Per lap</span>
            <span>up to {num(lim.callsPerLap)} of the organisation's 4,000 hourly calls — {num(lim.callsPerOpp)} reads per opportunity plus one per document (cap {num(lim.maxDocsPerOpp)} a bid, {Math.round((lim.maxDocBytes || 0) / 1024 / 1024)} MB a file)</span>
          </div>
          {e.lastDocExamples?.length > 0 && (
            <div style={{ marginTop: 8 }}>
              <div style={{ fontSize: 12, color: B.textMd, fontWeight: 600 }}>What the document links answered (the last lap's first {e.lastDocExamples.length})</div>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12, marginTop: 4 }}>
                <thead><tr style={{ color: B.textMd, textAlign: 'left' }}>
                  <th style={{ padding: '4px 6px' }}>Document</th><th style={{ padding: '4px 6px' }}>Outcome</th><th style={{ padding: '4px 6px' }}>Host</th>
                  <th style={{ padding: '4px 6px' }}>HTTP</th><th style={{ padding: '4px 6px' }}>Content type</th><th style={{ padding: '4px 6px' }}>What came back</th>
                </tr></thead>
                <tbody>{e.lastDocExamples.map((x, i) => (
                  <tr key={i} style={{ borderTop: `1px solid ${B.border}` }}>
                    <td style={{ padding: '4px 6px', maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={x.name || ''}>{x.name || '—'}</td>
                    <td style={{ padding: '4px 6px', color: x.reason === 'landed' ? B.green : B.amber }}>{docOutcomeLabel(x.reason)}</td>
                    <td style={{ padding: '4px 6px', fontFamily: 'monospace' }}>{x.host || '—'}</td>
                    <td style={{ padding: '4px 6px' }}>{x.status ?? '—'}</td>
                    <td style={{ padding: '4px 6px', fontFamily: 'monospace' }}>{x.contentType || '—'}</td>
                    <td style={{ padding: '4px 6px', color: B.textMd }}>{x.title ? `“${x.title}”` : (x.snippet || '—')}</td>
                  </tr>
                ))}</tbody>
              </table>
            </div>
          )}
          {e.lastError && <div style={note(e.lastLapStop === 'rate-limited' ? 'warn' : 'error')}>Last lap ended with: {e.lastError}{e.lastLapStop === 'rate-limited' ? ' — the organisation\'s hourly GovWin allowance was spent; the row was left for the next lap.' : ''}</div>}
        </div>
      )}
      {!e.available && <div style={note('warn')}>{e.reason || 'The enrichment columns are missing.'} Enrichment runs once the deploy has applied it.</div>}
    </div>
  );
};

const ConfigModal = ({ status, onClose, onSaved }) => {
  const { toast } = useToast();
  const [clientId, setClientId]       = useState(status?.clientId || '');
  const [clientSecret, setClientSecret] = useState('');
  const [username, setUsername]       = useState(status?.username || '');
  const [password, setPassword]       = useState('');
  const [searches, setSearches]       = useState((status?.savedSearchIds || []).join(', '));
  const [includeMarked, setIncludeMarked] = useState(status?.includeMarked !== false);
  const [includeKeywords, setIncludeKeywords] = useState(status?.includeKeywords !== false);
  const [enrich, setEnrich]           = useState(status?.enrich !== false);
  const [enabled, setEnabled]         = useState(status?.enabled !== false);
  const [seedDays, setSeedDays]       = useState(String(status?.seedDays || 30));
  const [saving, setSaving]           = useState(false);
  const [err, setErr]                 = useState(null);

  const save = async () => {
    setSaving(true); setErr(null);
    try {
      const body = { clientId, username, savedSearchIds: searches, includeMarked, includeKeywords, enrich, enabled, seedDays: Number(seedDays) || 30 };
      if (clientSecret) body.clientSecret = clientSecret;
      if (password)     body.password     = password;
      const res = await api.put('govwins/iq/config', body);
      toast.success(res?.credentialsChanged ? 'GovWin IQ credentials saved — run Test connection.' : 'GovWin IQ settings saved.');
      onSaved?.();
      onClose();
    } catch (e) { setErr(e.message); }
    finally { setSaving(false); }
  };

  return (
    <Modal title="GovWin IQ connection" onClose={onClose} width={620}>
      <div style={{ fontSize: 13, color: B.textMd, marginBottom: 12 }}>
        From GovWin IQ: profile → <b>Integrations → Web Services Administration</b> → Enable New Application
        (type <i>GovWin IQ Webservices API</i>) gives the client id and secret. The username and password are
        the GovWin IQ <b>user</b> the pull runs as — its saved searches are the ones the API can read.
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
        <Input label="Client ID" value={clientId} onChange={setClientId} required />
        <Input label={status?.hasClientSecret ? 'Client secret (blank keeps stored)' : 'Client secret'} type="password" value={clientSecret} onChange={setClientSecret} required={!status?.hasClientSecret} />
        <Input label="Web-service username" value={username} onChange={setUsername} placeholder="webservices@harvus.com" required />
        <Input label={status?.hasPassword ? 'Password (blank keeps stored)' : 'Password'} type="password" value={password} onChange={setPassword} required={!status?.hasPassword} />
      </div>
      <div style={{ marginTop: 12 }}>
        <Input label="Saved search ids (optional, comma-separated)" value={searches} onChange={setSearches} placeholder="e.g. 123456, 234567" />
        <div style={{ fontSize: 12, color: B.textLt, marginTop: 4 }}>
          Optional. A saved search must belong to the web-service user above, and that account is often API-only
          (it cannot sign in to iq.govwin.com). If you cannot log in as it, leave this blank — the keyword pull below
          needs no GovWin login. To use one anyway: sign in as that user, open <b>My Saved Searches</b>, edit a search,
          and read the id from the URL (<code>…/neo/search?ss=<b>123456</b></code>).
        </div>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginTop: 12 }}>
        <Input label="First-sync lookback (days)" type="number" value={seedDays} onChange={setSeedDays} />
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, justifyContent: 'flex-end', fontSize: 13 }}>
          <label style={{ display: 'flex', gap: 8, alignItems: 'center', cursor: 'pointer' }}>
            <input type="checkbox" checked={includeKeywords} onChange={e => setIncludeKeywords(e.target.checked)} />
            Pull by the Gov Opportunities keywords / NAICS codes — or <b>everything</b> new &amp; updated when that list is empty (no login needed)
          </label>
          <label style={{ display: 'flex', gap: 8, alignItems: 'center', cursor: 'pointer' }}>
            <input type="checkbox" checked={includeMarked} onChange={e => setIncludeMarked(e.target.checked)} />
            Also pull opportunities marked “Add to Web Services Download”
          </label>
          <label style={{ display: 'flex', gap: 8, alignItems: 'center', cursor: 'pointer' }}>
            <input type="checkbox" checked={enrich} onChange={e => setEnrich(e.target.checked)} />
            Pull <b>everything</b> GovWin holds on each opportunity — details, contacts, related documents, notices, contracts, milestones — one bounded lap an hour
          </label>
          <label style={{ display: 'flex', gap: 8, alignItems: 'center', cursor: 'pointer' }}>
            <input type="checkbox" checked={enabled} onChange={e => setEnabled(e.target.checked)} />
            Run on the daily Gov Opportunities job
          </label>
        </div>
      </div>
      <div style={note('info')}>
        The sync itself reads summaries only — one API call per page of 100, against the organisation’s 4,000-calls-per-hour limit.
        The “everything” pull is a separate hourly job bounded to its own share of that hour, and SAM.gov attachments still come by solicitation number.
      </div>
      {err && <div style={note('error')}>{err}</div>}
      <ModalFooter>
        <Btn variant="secondary" onClick={onClose}>Cancel</Btn>
        <Btn onClick={save} loading={saving} disabled={!clientId.trim() || !username.trim()}>Save</Btn>
      </ModalFooter>
    </Modal>
  );
};

const SyncSummary = ({ s }) => {
  if (!s) return null;
  const searches = (s.searches || []).concat(s.keywords || [], s.marked ? [s.marked] : []);
  return (
    <div style={{ marginTop: 12 }}>
      <div style={row}>
        <span style={lbl}>Last sync</span><span>{dt(s.completedUTC || s.startedUTC)} · {s.triggeredBy}{s.full ? ' · full re-read' : ''}{s.budgetMs ? ` · ${Math.round(s.budgetMs / 60000)}-minute budget` : ''}</span>
        <span style={lbl}>Result</span>
        <span>{s.fetched ?? 0} fetched · {s.upserted ?? 0} upserted · <b>{s.inserted ?? 0} new</b> · {s.docsPulled ?? 0} SAM.gov docs · {s.calls ?? 0} API calls · {stopLabel(s.stop)}</span>
        {s.from && <><span style={lbl}>Read from</span><span>{s.from === '01/01/1900' ? 'everything the searches match' : <>{wall(s.from)} at the oldest — each search reads from its own last complete read (below)</>}</span></>}
        {s.resumeLabel && <><span style={lbl}>Next run resumes at</span><span>{s.resumeLabel} — the keyword list is rotated so the searches this run never reached go first</span></>}
        {s.unfinished?.length > 0 && !s.resumeLabel && <><span style={lbl}>Not finished</span><span>{s.unfinished.length} search{s.unfinished.length === 1 ? '' : 'es'} kept their place and will be re-read next run</span></>}
      </div>
      {searches.length > 0 && (
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12, marginTop: 8 }}>
          <thead><tr style={{ color: B.textMd, textAlign: 'left' }}>
            <th style={{ padding: '4px 6px' }}>Source</th><th style={{ padding: '4px 6px' }}>Read from</th><th style={{ padding: '4px 6px', textAlign: 'right' }}>Matched</th>
            <th style={{ padding: '4px 6px', textAlign: 'right' }}>Fetched</th><th style={{ padding: '4px 6px', textAlign: 'right' }}>New</th>
            <th style={{ padding: '4px 6px' }}>Stopped</th>
          </tr></thead>
          <tbody>{searches.map(r => (
            <tr key={r.label} style={{ borderTop: `1px solid ${B.border}` }}>
              <td style={{ padding: '4px 6px' }}>{r.label}</td>
              <td style={{ padding: '4px 6px', color: B.textMd, whiteSpace: 'nowrap' }}>{r.from ? (r.from === '01/01/1900' ? 'everything' : wall(r.from)) : 'whole list'}</td>
              <td style={{ padding: '4px 6px', textAlign: 'right' }}>{r.totalCount ?? '—'}</td>
              <td style={{ padding: '4px 6px', textAlign: 'right' }}>{r.fetched}</td>
              <td style={{ padding: '4px 6px', textAlign: 'right' }}>{r.inserted}</td>
              <td style={{ padding: '4px 6px', color: r.error ? B.red : B.textMd }}>{r.error || stopLabel(r.stop)}</td>
            </tr>
          ))}</tbody>
        </table>
      )}
      {s.errors?.length > 0 && <div style={note('error')}>{s.errors.join(' · ')}</div>}
    </div>
  );
};

export const GovWinIqPanel = () => {
  const { toast } = useToast();
  const { data, loading, error, reload } = useApi(() => api.get('govwins/iq/status'), []);
  const [configOpen, setConfigOpen] = useState(false);
  const [testing, setTesting]   = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [syncing, setSyncing]   = useState(false);
  const [syncError, setSyncError] = useState(null);

  const test = async () => {
    setTesting(true); setTestResult(null);
    try { setTestResult(await api.post('govwins/iq/test', {})); reload(); }
    catch (e) { setTestResult({ ok: false, error: e.message }); }
    finally { setTesting(false); }
  };

  const sync = async (full) => {
    if (full && !window.confirm('Re-read everything the saved searches match, back to the beginning? This spends more of the hourly API budget and can take several minutes.')) return;
    setSyncing(true); setSyncError(null);
    try {
      const s = await api.post('govwins/iq/sync', { full: !!full }, { timeout: SYNC_TIMEOUT_MS });
      toast.success(`GovWin IQ: ${s.upserted ?? 0} upserted, ${s.inserted ?? 0} new${s.partial ? ' (partial — more remains)' : ''}`);
    } catch (e) {
      // The run is persisted server-side, so a timed-out response is not a
      // failed sync; status below is the truth.
      setSyncError(e.message);
    } finally { setSyncing(false); reload(); }
  };

  if (loading) return <Spinner />;
  if (error)   return <ErrorMsg msg={error} onRetry={reload} />;
  const s = data || {};
  const connected = s.configured && s.token?.connected && !s.needsReconnect;

  return (
    <Card style={{ padding: 16, marginBottom: 16 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
        <div>
          <div style={{ fontSize: 15, fontWeight: 700, color: B.text, display: 'flex', alignItems: 'center', gap: 8 }}>
            GovWin IQ API
            {!s.configured && <Badge color={B.textLt}>not configured</Badge>}
            {s.configured && s.needsReconnect && <Badge color={B.red}>reconnect</Badge>}
            {s.configured && !s.needsReconnect && (connected ? <Badge color={B.green}>connected</Badge> : <Badge color={B.amber}>not yet authenticated</Badge>)}
            {s.configured && !s.enabled && <Badge color={B.amber}>paused</Badge>}
          </div>
          <div style={{ fontSize: 12, color: B.textMd, marginTop: 2 }}>
            Pulls GovWin IQ straight into Gov Opportunities on the daily run — by your keyword list, by saved search, and by the org's download marks. No forwarded email needed.
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <Btn variant="secondary" size="sm" onClick={() => setConfigOpen(true)}><Icon name="settings" size={14} /> {s.configured ? 'Edit' : 'Configure'}</Btn>
          <Btn variant="secondary" size="sm" onClick={test} loading={testing} disabled={!s.configured}>Test connection</Btn>
          <Btn size="sm" onClick={() => sync(false)} loading={syncing} disabled={!s.configured}><Icon name="refresh" size={14} /> Sync now</Btn>
          <Btn variant="ghost" size="sm" onClick={() => sync(true)} disabled={!s.configured || syncing}>Full re-read</Btn>
        </div>
      </div>

      {s.configured && (
        <div style={{ ...row, marginTop: 12 }}>
          <span style={lbl}>Client ID</span><span style={{ fontFamily: 'monospace' }}>{s.clientId}{s.sources?.clientId === 'env' ? <span style={{ color: B.textLt }}> (from environment)</span> : ''}</span>
          <span style={lbl}>Web-service user</span><span>{s.username}</span>
          <span style={lbl}>Saved searches</span>
          <span>{s.savedSearchIds?.length ? s.savedSearchIds.join(', ') : <span style={{ color: B.textLt }}>none</span>}</span>
          <span style={lbl}>Also pulls</span>
          <span>{[s.includeKeywords && (s.lastSync?.keywordCount ? `the ${s.lastSync.keywordCount} Gov Opportunities keyword(s)` : 'every opportunity new or updated since the last sync (the keyword list is empty)'), s.includeMarked && 'opportunities marked for Web Services download'].filter(Boolean).join(' · ') || <span style={{ color: B.amber }}>nothing else — turn a pull on in Edit</span>}</span>
          <span style={lbl}>Access token</span>
          <span>{s.token?.state === 'valid' ? `valid until ${dt(s.token.accessExpiresUTC)}` : s.token?.state === 'refresh' ? `expired — will refresh (refresh token good until ${dt(s.token.refreshExpiresUTC)})` : 'none — the next call authenticates'}</span>
          <span style={lbl}>Caught up through</span><span>{s.lastSyncUTC ? <>{dt(s.lastSyncUTC)} on every search — a search cut off since then re-reads from its own last complete read, minus a day of overlap</> : `nothing yet — the first sync reads the last ${s.seedDays} days`}</span>
        </div>
      )}

      {s.needsReconnect && (
        <div style={note('error')}>
          GovWin refused the credentials: {s.needsReconnect}. Check the password for {s.username} and that the client id belongs to this organisation, then save and Test again. Five refusals in a row lock the GovWin account for 30 minutes.
        </div>
      )}
      {!s.configured && (
        <div style={note('info')}>
          Nothing is pulled until the client id, client secret, web-service username and password are saved here. The email forward and the spreadsheet import keep working regardless.
        </div>
      )}
      {testResult && (
        <div style={note(testResult.ok ? 'success' : 'error')}>
          {testResult.ok
            ? <>Authenticated and read {testResult.probe}: {testResult.totalCount ?? '?'} matching opportunit{testResult.totalCount === 1 ? 'y' : 'ies'}{testResult.sample ? <> — e.g. “{testResult.sample.title}” ({testResult.sample.status || 'no status'}).</> : '.'}</>
            : <>Test failed: {testResult.error}</>}
        </div>
      )}
      {syncError && <div style={note('warn')}>The sync request ended with “{syncError}” — the run continues server-side and its result appears below when it finishes; refresh to check.</div>}

      <SyncSummary s={s.lastSync} />

      {s.configured && s.enrichment && <EnrichmentSection s={s} reload={reload} />}

      {configOpen && <ConfigModal status={s} onClose={() => setConfigOpen(false)} onSaved={reload} />}
    </Card>
  );
};
